import React, { useState, useEffect, FC } from "react";
import { Employee } from "../models";
import {
  Box,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  IconButton,
  FormControlLabel,
  Checkbox,
  Stack,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import orgDataService from "services/orgDataService";
import { Hub, KeyValue } from "../../common/models";
import employeeService from "services/employeeService";
import MDButton from "components/MDButton";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { toast } from "react-toastify";
import { DataGrid, GridCheckIcon, GridColDef, GridRenderCellParams, GridCloseIcon } from "@mui/x-data-grid";

export interface EmployeeHubProps {
  employee: Employee;
  setEmployee(employee: Employee): any;
  reloadEmployee(): any;
}

const EmployeeHubs: FC<EmployeeHubProps> = (props) => {
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [filteredHubs, setFilteredHubs] = useState<Hub[]>([]);
  const [selectedHub, setSelectedHub] = useState<number>();
  const [dispatchContactAllowed, setDispatchContactAllowed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const columns: GridColDef[] = [
    { field: "name", headerName: "Name", flex:1},
    { field: "dispatchContactAllowed", headerName: "Dispatch Contact Allowed", sortable: false, flex:1, renderCell: (params: GridRenderCellParams<any, boolean>) => params.value ? <GridCheckIcon style={{fill: "green"}}/>: <GridCloseIcon style={{fill: "red"}}/>},
    { field: "",  width: 90 ,sortable:false, renderCell: (params) =>  <IconButton title="Delete" onClick={() => deleteHub(params.id)} sx={{ ml: 2 }}>
    <DeleteIcon></DeleteIcon>
  </IconButton>},

  ];
  

  useEffect(() => {
    orgDataService.getAllHubs().then((data) => {
      setHubs(data);
      setFilteredHubs(data.filter((d) => !props.employee.hubs.some((h) => h.id == d.id)));
    });
  }, []);

  useEffect(() => {
    setFilteredHubs(hubs.filter((d) => !props.employee.hubs.some((h) => h.id == d.id)));
  }, [props.employee.hubs]);

  const addHub = async () => {
    if (!selectedHub) return;
    setLoading(true);
    try {
      await employeeService.addEmployeeHub(props.employee.id, +selectedHub, dispatchContactAllowed);
      await props.reloadEmployee();
      toast.success("Hub Added");
    } finally {
      setLoading(false);
    }
  };

  const addAllHubs = async () => {
    // Recompute at click time; backend also dedupes, but skip the round-trip if nothing to do.
    const existingIds = new Set(props.employee.hubs.map((h) => h.id));
    const toAddIds = hubs.filter((h) => !existingIds.has(h.id)).map((h) => h.id);
    if (toAddIds.length === 0) {
      toast.info("Employee already has every hub.");
      return;
    }
    setLoading(true);
    try {
      await employeeService.addEmployeeHubsBulk(props.employee.id, toAddIds, dispatchContactAllowed);
      await props.reloadEmployee();
      toast.success(`Added ${toAddIds.length} hub${toAddIds.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error("Error adding all hubs");
    } finally {
      setLoading(false);
    }
  };

  const deleteHub = async (hubId: any) => {
    setLoading(true);
    try {
      await employeeService.deleteEmployeeHub(props.employee.id, +hubId);
      await props.reloadEmployee();
      toast.success("Hub Removed");
    } finally {
      setLoading(false);
    }
  };

  const removeAllHubs = async () => {
    if (props.employee.hubs.length === 0) return;
    if (!window.confirm(`Remove all ${props.employee.hubs.length} hubs from this employee?`)) return;
    setLoading(true);
    try {
      await employeeService.removeEmployeeHubsBulk(
        props.employee.id,
        props.employee.hubs.map((h) => h.id)
      );
      await props.reloadEmployee();
      toast.success("All hubs removed");
    } catch (e) {
      toast.error("Error removing all hubs");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Grid item xs={12} mt={2}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h5">Employee Hubs</MDTypography>
          </MDBox>
          <MDBox component="form" pb={3} px={3}>
            <Grid container spacing="2">
              <FormControl sx={{ mt: 2, ml: 1, minWidth: 130 }}>
                <InputLabel id="hubId">Hub</InputLabel>
                <Select
                  id="hubId"
                  name="hubId"
                  labelId="hubId"
                  label="hub"
                  onChange={(e: any) => setSelectedHub(e.target.value)}
                >
                  {hubs &&
                    filteredHubs.map((h, index) => {
                      return (
                        <MenuItem key={index} value={h.id}>
                          {h.name}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
              <FormControlLabel
                sx={{ ml: 1, mt: 2 }}
                control={
                  <Checkbox
                    checked={dispatchContactAllowed}
                    onChange={(e: any) => setDispatchContactAllowed(e.target.checked)}
                  />
                }
                label="Dispatch Contact Allowed"
              />

              <MDButton
                variant="contained"
                sx={{ mt: 2, ml: 2, height: 46 }}
                color="dark"
                onClick={addHub}
                disabled={loading}
              >
                Add
              </MDButton>
              <Tooltip
                title={
                  filteredHubs.length === 0
                    ? "User has all hubs"
                    : "Assign every remaining hub to this employee"
                }
              >
                <span style={{ display: "inline-flex" }}>
                  <MDButton
                    variant="outlined"
                    sx={{ mt: 2, ml: 1, height: 46 }}
                    color="dark"
                    onClick={addAllHubs}
                    disabled={loading || filteredHubs.length === 0}
                  >
                    Add All
                  </MDButton>
                </span>
              </Tooltip>
              {props.employee.hubs.length > 0 && (
                <MDButton
                  variant="outlined"
                  sx={{ mt: 2, ml: 1, height: 46 }}
                  color="error"
                  onClick={removeAllHubs}
                  disabled={loading}
                  title="Remove every hub from this employee"
                >
                  Remove All
                </MDButton>
              )}
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 2, overflow: "hidden" }}>
                
                  <DataGrid
                  autoHeight={ true}
                    loading={loading}
                    sx={{ overflow: "hidden", fontSize: ".95em" }}
                    rows={props.employee.hubs}
                    columns={columns}
                    initialState={{
                      pagination: { paginationModel: {page: 0, pageSize:10} },
                      sorting: { sortModel: [{ field: "name", sort: "asc" }] },
                    }}
                    slots={{
                      noRowsOverlay: () => (
                        <Stack height="100%" alignItems="center" justifyContent="center">
                          No Hubs
                        </Stack>
                      ),
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    disableColumnMenu
                    density="compact"
                    getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
                  />
              </Box>
            </Grid>
          </MDBox>
        </Card>
      </Grid>
    </>
  );
};

export default EmployeeHubs;
